import React from "react";
import {
    Flex,
    Text,
    Box,
    VStack,
    HStack,
    Icon,
    Button,
    Card,
    CardBody,
    Heading,
    Link,
    Badge,
    SimpleGrid,
} from "@chakra-ui/react";
import {
    MdEmail,
    MdPhone,
    MdChat,
    MdHelp,
    MdFeedback,
    MdSupport,
    MdInfo,
} from "react-icons/md";

const SupportPage = () => {
    console.log("SupportPage component rendered successfully");

    try {
        return (
            <Box p={6} maxW="1200px" mx="auto" minH="80vh">
                <Flex align="center" mb={6}>
                    <Icon as={MdSupport} boxSize={8} color="blue.500" mr={3} />
                    <Heading as="h1" size="xl" color="gray.700">
                        Support Center
                    </Heading>
                </Flex>

                <VStack spacing={6} align="stretch">
                    {/* Quick Stats */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Card>
                            <CardBody textAlign="center">
                                <Icon as={MdInfo} boxSize={6} color="blue.500" mb={2} />
                                <Text fontWeight="bold" fontSize="lg">24/7 Support</Text>
                                <Text fontSize="sm" color="gray.600">Available round the clock</Text>
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody textAlign="center">
                                <Icon as={MdChat} boxSize={6} color="green.500" mb={2} />
                                <Text fontWeight="bold" fontSize="lg">Live Chat</Text>
                                <Text fontSize="sm" color="gray.600">Instant responses</Text>
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody textAlign="center">
                                <Icon as={MdEmail} boxSize={6} color="purple.500" mb={2} />
                                <Text fontWeight="bold" fontSize="lg">Email Support</Text>
                                <Text fontSize="sm" color="gray.600">Detailed assistance</Text>
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Contact Options */}
                    <Card>
                        <CardBody>
                            <Heading size="md" mb={4}>Get in Touch</Heading>
                            <VStack spacing={4} align="stretch">
                                <HStack spacing={4} p={3} bg="blue.50" borderRadius="md">
                                    <Icon as={MdEmail} boxSize={6} color="blue.500" />
                                    <Box flex={1}>
                                        <Text fontWeight="semibold">Email Support</Text>
                                        <Link href="mailto:support@yourcompany.com" color="blue.500">
                                            support@yourcompany.com
                                        </Link>
                                        <Badge colorScheme="blue" size="sm" ml={2}>
                                            Response within 24 hours
                                        </Badge>
                                    </Box>
                                </HStack>

                                <HStack spacing={4} p={3} bg="green.50" borderRadius="md">
                                    <Icon as={MdPhone} boxSize={6} color="green.500" />
                                    <Box flex={1}>
                                        <Text fontWeight="semibold">Phone Support</Text>
                                        <Text color="gray.600">+1 (555) 123-4567</Text>
                                        <Badge colorScheme="green" size="sm">
                                            Mon-Fri 9AM-6PM EST
                                        </Badge>
                                    </Box>
                                </HStack>

                                <HStack spacing={4} p={3} bg="purple.50" borderRadius="md">
                                    <Icon as={MdChat} boxSize={6} color="purple.500" />
                                    <Box flex={1}>
                                        <Text fontWeight="semibold">Live Chat</Text>
                                        <Button size="sm" colorScheme="purple" variant="outline" mt={2}>
                                            Start Chat Now
                                        </Button>
                                        <Badge colorScheme="purple" size="sm" ml={2}>
                                            Instant response
                                        </Badge>
                                    </Box>
                                </HStack>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Support Categories */}
                    <Card>
                        <CardBody>
                            <Heading size="md" mb={4}>How can we help you today?</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <Box
                                    p={4}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.50", borderColor: "blue.300" }}
                                    cursor="pointer"
                                    transition="all 0.2s"
                                >
                                    <HStack spacing={3}>
                                        <Icon as={MdHelp} boxSize={5} color="orange.500" />
                                        <Box>
                                            <Text fontWeight="semibold">General Help</Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Get help with basic features and functionality
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box
                                    p={4}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.50", borderColor: "red.300" }}
                                    cursor="pointer"
                                    transition="all 0.2s"
                                >
                                    <HStack spacing={3}>
                                        <Box>
                                            <Text fontWeight="semibold">Report a Bug</Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Let us know about any issues you've encountered
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box
                                    p={4}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.50", borderColor: "blue.300" }}
                                    cursor="pointer"
                                    transition="all 0.2s"
                                >
                                    <HStack spacing={3}>
                                        <Icon as={MdFeedback} boxSize={5} color="blue.500" />
                                        <Box>
                                            <Text fontWeight="semibold">Feature Request</Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Suggest new features or improvements
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box
                                    p={4}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.50", borderColor: "green.300" }}
                                    cursor="pointer"
                                    transition="all 0.2s"
                                >
                                    <HStack spacing={3}>
                                        <Icon as={MdSupport} boxSize={5} color="green.500" />
                                        <Box>
                                            <Text fontWeight="semibold">Technical Support</Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Get help with technical issues and setup
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>
                            </SimpleGrid>
                        </CardBody>
                    </Card>

                </VStack>
            </Box>
        );
    } catch (error) {
        console.error("Error in SupportPage component:", error);
        return (
            <Box p={6}>
                <Text color="red.500">Error loading Support page</Text>
            </Box>
        );
    }
};

export default SupportPage;